'use client';
import { isTjtSettings } from '../data/publicationIdentity';
import { storedNavigationItems } from "@/lib/publications/navigation";
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { defaultTaxonomy } from '../data/taxonomy';
import { tjtEditions } from '@/lib/publications/registry';
import ReaderAuthControl from './ReaderAuthControl';
import { X } from 'lucide-react';

export default function StickyNavigationDrawer({ isOpen, onClose, siteSettings, publishedArticles = [], routePrefix }) {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const closeRef = useRef(null);
  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setExpandedIndex(null);
      requestAnimationFrame(() => closeRef.current?.focus());
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Escape key close
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const buildUrl = (path) => routePrefix ? `/${routePrefix}${path}` : path;

  // Build nav items
  let navItems = defaultTaxonomy.map(cat => ({
    label: cat.label,
    url: buildUrl(`/category/${cat.label.toLowerCase().replace(/\s+/g, '-')}`),
    children: cat.children.map(child => ({
      label: child,
      url: buildUrl(`/category/${cat.label.toLowerCase().replace(/\s+/g, '-')}/${child.toLowerCase().replace(/\s+/g, '-')}`)
    }))
  }));

  const dynamicCategories = Array.from(new Set(publishedArticles.map(a => a.category).filter(Boolean)));
  dynamicCategories.forEach(cat => {
    if (!navItems.find(item => item.label === cat)) {
      navItems.push({
        label: cat,
        url: buildUrl(`/category/${cat.toLowerCase().replace(/\s+/g, '-')}`),
        children: []
      });
    }
  });

  if (Array.isArray(siteSettings?.navigation?.header)) {
    navItems = storedNavigationItems(siteSettings.navigation.header, siteSettings).map(item => ({ ...item, url: item.href,
      children: item.children.map(child => ({ ...child, url: child.href })) }));
  }

  const handleEditionSwitch = (e, edition) => {
    e.preventDefault();
    document.cookie = `tjt_preferred_edition=${edition.preferenceCode}; path=/; max-age=31536000; samesite=lax`;
    window.location.href = edition.prefix ? `/${edition.prefix}/` : '/';
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/40 z-[101]" 
        onClick={onClose}
        aria-hidden="true"
      />
      <div 
        className="fixed inset-y-0 left-0 w-[320px] sm:w-[380px] max-w-full bg-white z-[102] border-r border-gray-300 shadow-xl overflow-y-auto flex flex-col transition-transform duration-200 transform translate-x-0"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <span className="font-classic font-bold text-xl uppercase tracking-wider">Sections</span>
          <button ref={closeRef} onClick={onClose} aria-label="Close navigation menu" className="p-2 text-gray-500 hover:text-black">
            <X size={24} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 py-4">
          <nav className="px-6 flex flex-col">
            {navItems.map((item, i) => (
              <div key={i} className="flex flex-col border-b border-gray-200">
                {item.children && item.children.length > 0 && (
                  <button type="button" onClick={() => setExpandedIndex(expandedIndex === i ? null : i)} aria-expanded={expandedIndex === i} aria-controls={`mobile-section-${i}`} className="flex w-full items-center justify-between py-4 text-left text-base font-bold uppercase tracking-wide hover:text-gray-600">
                    {item.label}<span aria-hidden="true" className="text-xl font-normal">{expandedIndex === i ? '⌄' : '›'}</span>
                  </button>
                )}
                {(!item.children || item.children.length === 0) && (
                  <Link href={item.url} onClick={onClose} className="py-4 text-base font-bold uppercase tracking-wide hover:text-gray-600">{item.label}</Link>
                )}
                {item.children && item.children.length > 0 && expandedIndex === i && (
                  <div id={`mobile-section-${i}`} className="flex flex-col space-y-3 border-l-2 border-gray-200 pb-4 pl-4">
                    <Link href={item.url} onClick={onClose} className="text-sm font-black uppercase tracking-wider text-black hover:underline">All {item.label}</Link>
                    {item.children.map((child, j) => (
                      <Link key={j} href={child.url} onClick={onClose} className="text-sm font-medium font-sans text-gray-600 hover:text-black transition-colors">
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {isTjtSettings(siteSettings) && <div className="mt-8 px-6 pt-6 border-t border-gray-200">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Editions</h4>
            <div className="flex flex-col space-y-3">
              {tjtEditions.map(edition => {
                const isActive = edition.prefix === routePrefix || (!edition.prefix && !routePrefix);
                return (
                  <a
                    key={edition.preferenceCode}
                    href={edition.prefix ? `/${edition.prefix}/` : '/'}
                    onClick={(e) => handleEditionSwitch(e, edition)}
                    className={`font-sans text-sm uppercase tracking-wide transition-colors ${
                      isActive ? 'font-bold text-black' : 'text-gray-500 hover:text-black'
                    }`}
                  >
                    {edition.name} {isActive && <span className="ml-2 text-[10px] bg-black text-white px-1.5 py-0.5 rounded align-middle">Active</span>}
                  </a>
                );
              })}
            </div>
          </div>}
          
          <div className="mt-8 px-6 pt-6 pb-8 border-t border-gray-200">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Reader Account</h4>
            <div className="text-sm uppercase tracking-wide font-sans">
              <ReaderAuthControl currentPrefix={routePrefix} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
